<template>
  <v-app>

    <!-- ================= SEARCH PANEL ================= -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col md="2" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="From Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col md="2" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="To Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col md="2" cols="12">
                      <v-btn type="submit" color="primary" class="mt-1">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- ================= TABLE PANEL ================= -->
    <v-container fluid style="margin-top:-20px">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Proforma Invoice Records
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <!-- Top Actions -->
                <v-row class="py-2 px-2">
                  <v-col md="4">
                    <v-text-field
                      v-model="search"
                      label="Search"
                      append-inner-icon="mdi-magnify"
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                    />
                  </v-col>

                  <v-col md="5" class="text-center">
                    <v-btn-group density="compact" divided>
                      <v-btn color="success" @click="exportToCSV">CSV</v-btn>
                      <v-btn color="primary" @click="exportToExcel">EXCEL</v-btn>
                      <v-btn color="red" @click="exportToPDF">PDF</v-btn>
                    </v-btn-group>
                  </v-col>

                  <v-col md="3" class="text-right">
                    <v-btn
                      variant="tonal"
                      color="primary"
                      prepend-icon="mdi-plus"
                      rounded="xl"
                      @click="goToCreate"
                    >
                      Add Proforma Invoice
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- ================= DATA TABLE ================= -->
                <v-data-table
                  :headers="headers"
                  :items="proformaInvoiceList"
                  :loading="loading"
                  :search="search"
                  v-model:items-per-page="itemsPerPage"
                  density="compact"
                >
                  <!-- Index -->
                  <template #item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <!-- Buyer -->
                  <template #item.buyer_name="{ item }">
                    {{ item.buyer?.company_owner_name || '-' }} - {{ item.buyer?.code || '-' }}
                  </template>

                  <!-- Shipment Mode -->
                  <template #item.shipment_mode="{ item }">
                    {{ item.shipment_mode_id || '-' }}
                  </template>

                  <!-- Status -->
                  <template #item.status="{ item }">
                    <v-chip
                      size="small"
                      :color="item.status === 1 ? 'success' : 'red'"
                    >
                      {{ item.status === 1 ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </template>

                  <!-- Actions -->
                  <template #item.actions="{ item }">
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      color="primary"
                      @click="goToEdit(item.id)"
                    />
                    <v-btn
                      icon="mdi-file"
                      size="small"
                      variant="text"
                      color="info"
                      @click="viewDetails(item.id)"
                    />
                  </template>
                </v-data-table>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- ================= DETAILS DIALOG ================= -->
    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card v-if="selectedItem">
        <v-card-title class="d-flex justify-space-between">
          Proforma Invoice Details
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog=false" />
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col md="6"><b>Code:</b> {{ selectedItem.code }}</v-col>
            <v-col md="6"><b>Invoice No:</b> {{ selectedItem.proforma_invoice_no }}</v-col>
            <v-col md="6"><b>Buyer:</b> {{ selectedItem.buyer?.company_owner_name }}</v-col>
            <v-col md="6"><b>Shipment Mode:</b> {{ selectedItem.shipment_mode_id }}</v-col>
            <v-col md="6"><b>Order Date:</b> {{ selectedItem.invoice_date }}</v-col>
            <v-col md="6"><b>Status:</b> {{ selectedItem.status === 1 ? 'Active' : 'Inactive' }}</v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="detailsDialog=false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-app>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

const store = useStore();
const router = useRouter();

/* ================= STATE ================= */
const panel = ref([0]);
const search = ref("");
const loading = ref(false);
const itemsPerPage = ref(10);
const detailsDialog = ref(false);
const selectedItem = ref(null);

/* ================= FORM ================= */
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable(),
    to_date: yup.date().nullable(),
  }),
});

const from_date = useField("from_date");
const to_date = useField("to_date");

/* ================= TABLE HEADERS ================= */
const headers = [
  { title: "#", key: "index", sortable: false },
  { title: "Code", key: "code" },
  { title: "Invoice No", key: "proforma_invoice_no" },
  { title: "Invoice Date", key: "invoice_date" },
  { title: "Buyer", key: "buyer_name" },
  { title: "Currrency", key: "currency.code" },
  { title: "Shipment Mode", key: "shipping_mode.name" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions", sortable: false },
];

/* ================= COMPUTED ================= */
const proformaInvoiceList = computed(() => store.getters.getProformaInvoiceList || []);

/* ================= METHODS ================= */
const fetchList = async (payload = {}) => {
  loading.value = true;
  try {
    await store.dispatch("getProformaInvoiceList", payload);
  } catch (e) {
    toast.error("Failed to load records");
  } finally {
    loading.value = false;
  }
};

const submit = handleSubmit(() => {
  fetchList({
    from_date: from_date.value.value,
    to_date: to_date.value.value,
  });
});

const goToCreate = () => {
  router.push({ name: "proforma-invoice-create" });
};

const goToEdit = (id) => {
  router.push({ name: "proforma-invoice-create", params: { id } });
};

const viewDetails = (id) => {
  router.push({ name: "proforma-invoice-view", params: { id } });
};

const exportToCSV = () => toast.info("CSV export coming soon");
const exportToExcel = () => toast.info("Excel export coming soon");
const exportToPDF = () => toast.info("PDF export coming soon");

/* ================= ON MOUNT ================= */
onMounted(() => {
  fetchList();
});
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
